import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Icon } from '@column/column-ui-kit';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@column/ui';

import { RenderFields } from '~/components';
import { SectionWithFields } from '~/components/RenderFields';

type EntityType = '' | 'PERSON' | 'BUSINESS';
type VerificationStatusFilter = '' | 'UNVERIFIED' | 'PENDING' | 'MANUAL_REVIEW' | 'VERIFIED' | 'DENIED';

const TYPE_OPTIONS: { label: string; value: Exclude<EntityType, ''> }[] = [
  { label: 'Person', value: 'PERSON' },
  { label: 'Business', value: 'BUSINESS' },
];

const VERIFICATION_STATUS_OPTIONS: { label: string; value: Exclude<VerificationStatusFilter, ''> }[] = [
  { label: 'Unverified', value: 'UNVERIFIED' },
  { label: 'Pending', value: 'PENDING' },
  { label: 'Manual review', value: 'MANUAL_REVIEW' },
  { label: 'Verified', value: 'VERIFIED' },
  { label: 'Denied', value: 'DENIED' },
];

interface FormValues {
  type: EntityType;
  verificationStatus: VerificationStatusFilter;
  query: string;
}

interface EntityFiltersModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialType: EntityType;
  initialVerificationStatus: VerificationStatusFilter;
  initialQuery: string;
  onApply: (values: { type: EntityType; verificationStatus: VerificationStatusFilter; query: string }) => void;
}

export const EntityFiltersModal: React.FC<EntityFiltersModalProps> = ({
  isOpen,
  onClose,
  initialType,
  initialVerificationStatus,
  initialQuery,
  onApply,
}) => {
  const form = useForm<FormValues>({
    defaultValues: {
      type: initialType,
      verificationStatus: initialVerificationStatus,
      query: initialQuery,
    },
  });

  useEffect(() => {
    if (!isOpen) return;
    form.reset({
      type: initialType,
      verificationStatus: initialVerificationStatus,
      query: initialQuery,
    });
  }, [isOpen, initialType, initialVerificationStatus, initialQuery, form]);

  const handleApply = () => {
    const fv = form.getValues();
    onApply({ type: fv.type, verificationStatus: fv.verificationStatus, query: fv.query });
    onClose();
  };

  // Clears the staged filters back to "no filter"; the user still confirms with Apply.
  const handleReset = () => {
    form.reset({ type: '', verificationStatus: '', query: '' });
  };

  // One field per section so RenderFields draws a separator between each — the divider comes from
  // the top of every Section after the first, not from a per-field flag.
  const sections: SectionWithFields<FormValues, undefined>[] = [
    {
      fields: [
        {
          id: 'type',
          label: 'Type',
          // Force the stacked (label-on-top) layout so the narrow modal never flips to the
          // side-by-side layout its container query would otherwise trigger.
          oneColumn: true,
          children: ({ value, onChange }) => (
            <Select value={(value as string) ?? ''} onValueChange={onChange}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select type..." />
              </SelectTrigger>
              <SelectContent>
                {TYPE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ),
        },
      ],
    },
    {
      fields: [
        {
          id: 'verificationStatus',
          label: 'Status',
          oneColumn: true,
          children: ({ value, onChange }) => (
            <Select value={(value as string) ?? ''} onValueChange={onChange}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select status..." />
              </SelectTrigger>
              <SelectContent>
                {VERIFICATION_STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ),
        },
      ],
    },
    {
      fields: [
        {
          id: 'query',
          label: 'Search',
          oneColumn: true,
          children: ({ value, onChange, onBlur }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={(event) => onChange(event.target.value)}
              onBlur={onBlur}
              placeholder="Search entities..."
            />
          ),
        },
      ],
    },
  ];

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent showCloseButton={false} className="sm:max-w-sm">
        {/* Header laid out as a row so the title stays left-aligned at every breakpoint and the
            close button is vertically centered against it (rather than pinned to the card corner). */}
        <DialogHeader className="flex-row items-center justify-between text-left">
          <DialogTitle>Filters</DialogTitle>
          <DialogClose
            aria-label="Close"
            className="rounded-xs text-muted-foreground opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-hidden [&_svg]:pointer-events-none"
          >
            <Icon.Close style={{ '--icon-size': '16px' } as React.CSSProperties} />
          </DialogClose>
        </DialogHeader>

        {/* Full-bleed so each Section's own padding and separator align with the dialog frame:
            negate the dialog's p-6 and hand the sections a matching --page-margin. */}
        <FormProvider {...form}>
          <div className="-mx-6 -mt-2 [--page-margin:24px]">
            <RenderFields sections={sections} />
          </div>
        </FormProvider>

        {/* Stacked on mobile with Reset above Apply (plain flex-col, not the footer's default
            reverse); on sm+ they sit at opposite ends. */}
        <DialogFooter className="flex-col sm:flex-row sm:items-center sm:justify-between">
          <Button type="button" variant="secondary" size="sm" onClick={handleReset}>
            Reset
          </Button>
          <Button type="button" size="sm" onClick={handleApply}>
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
